import gql from 'graphql-tag';

import { executeGqlMutation } from 'src/graphql/client';
import { FRAGMENT_JOB_ID, waitForJobWithEntityResult } from 'src/graphql/job';
import type { ID } from 'src/lib/collection';
import { stringifyIfPresent } from 'src/lib/common';
import { getGqlFragmentName, getSingleProp } from 'src/lib/graphql';
import type {
  BackendMutationRephraseTextsForDocumentEditorArgs,
  BackendRecomposeRequest,
} from 'src/types/backend';

import { FRAGMENT_RECOMPOSE_REQUEST } from './schema';

type Variables = Omit<BackendMutationRephraseTextsForDocumentEditorArgs, 'async'>;
type Result = BackendRecomposeRequest;

interface Options {
  async?: boolean;
  abortSignal?: AbortSignal;
}

/**
 * "recompose" generation for short-form
 */
export const rephraseTextsForDocumentEditor = async (
  { structureInstructionsData, ...rest }: Variables,
  options: Options | null = null
) => {
  const resultFragment = FRAGMENT_RECOMPOSE_REQUEST;
  const fragment = options?.async ? FRAGMENT_JOB_ID : resultFragment;
  const mutation = gql`
    mutation RephraseTextsForDocumentEditor(
      $async: Boolean
      $projectId: ID!
      $originalVariationId: ID
      $assetType: String
      $generationTool: GenerationToolEnum!
      $brief: BriefInput
      $scoringMethod: ScoringMethodInput
      $productName: String
      $text: String!
      $recomposeMode: String
      $keywords: String
      $language: String!
      $languageTargetFormality: LanguageTargetFormalityEnum
      $numberOfVariations: Int!
      $debugAcPrompt: String
      $sourceType: String
      $onlyImproveScore: Boolean
      $originalScore: Int
      $originalScoreLanguage: String
      $customerPersonaId: ID
      $annotations: [SuggestionAnnotationInput!]
      $manualInstructions: String
      $tovInput: TovInput
      $forceZeroScore: Boolean
      $rephraseContext: JSON
      $structureInstructionsData: JSON
      $imageResourceId: ID
      $imageStyleId: ID
      $imageOverlayProperties: ImageOverlayTextInput
    ) {
      rephraseTextsForDocumentEditor(
        async: $async
        projectId: $projectId
        originalVariationId: $originalVariationId
        assetType: $assetType
        generationTool: $generationTool
        brief: $brief
        scoringMethod: $scoringMethod
        productName: $productName
        text: $text
        recomposeMode: $recomposeMode
        keywords: $keywords
        language: $language
        languageTargetFormality: $languageTargetFormality
        numberOfVariations: $numberOfVariations
        debugAcPrompt: $debugAcPrompt
        sourceType: $sourceType
        onlyImproveScore: $onlyImproveScore
        originalScore: $originalScore
        originalScoreLanguage: $originalScoreLanguage
        customerPersonaId: $customerPersonaId
        annotations: $annotations
        manualInstructions: $manualInstructions
        tovInput: $tovInput
        forceZeroScore: $forceZeroScore
        rephraseContext: $rephraseContext
        structureInstructionsData: $structureInstructionsData
        imageResourceId: $imageResourceId
        imageStyleId: $imageStyleId
        imageOverlayProperties: $imageOverlayProperties
      ) {
        ...${getGqlFragmentName(fragment)}
      }
    }
    ${fragment}
  `;

  const variables = {
    ...rest,
    async: options?.async ?? false,
    structureInstructionsData: stringifyIfPresent(structureInstructionsData),
  };

  const response = await executeGqlMutation({
    mutation,
    variables,
    options: {
      abortSignal: options?.abortSignal,
      traceAcRequests: true,
    },
  });

  const result = getSingleProp<Result | Result[]>(response.data);
  if (!options?.async) {
    // support old BE, which returns a single object instead of an array
    if (!result) {
      return [];
    }
    return Array.isArray(result) ? result : [result];
  }

  const job = Array.isArray(result) ? result[0] : result;
  return waitForJobWithEntityResult<Result>((job as { id: ID }).id, resultFragment, {
    abortSignal: options?.abortSignal,
    list: true,
  });
};
